import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FileText, Download, Shield, Clock } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: '4 Professional Templates',
      description: 'Choose from Riverwood Newsletter, Sample 1, Sample 2, or Newsletter 2022 formats.',
    },
    {
      icon: <Download className="w-8 h-8" />,
      title: 'PDF Download & Print',
      description: 'Generate print-ready PDF newsletters for distribution to your community.',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Content Control',
      description: 'You provide the content. We format it professionally. No AI-generated information.',
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: 'Real-time Preview',
      description: 'See your newsletter come to life as you edit with live preview functionality.',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <header className="px-6 py-4 flex justify-between items-center max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-emerald-700 rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-semibold text-gray-900">HOA Newsletter</span>
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={() => navigate('/login')}>
            Sign In
          </Button>
          <Button onClick={() => navigate('/register')} className="bg-emerald-700 hover:bg-emerald-800">
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-6 py-20 max-w-7xl mx-auto text-center">
        <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
          Create Professional HOA
          <br />
          <span className="text-emerald-700">Newsletters in Minutes</span>
        </h1>
        <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
          Generate beautifully formatted newsletters for your Home Owners&apos; Association.
          Choose from multiple templates, add your content, and download print-ready PDFs.
        </p>
        <div className="flex gap-4 justify-center">
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-emerald-700 hover:bg-emerald-800 px-8 py-6 text-lg"
          >
            Start Creating Free
          </Button>
          <Button
            size="lg"
            variant="outline"
            onClick={() => navigate('/login')}
            className="px-8 py-6 text-lg border-emerald-700 text-emerald-700 hover:bg-emerald-50"
          >
            View Templates
          </Button>
        </div>
        <p className="mt-4 text-sm text-gray-500">
          2 newsletters per 30 days. No credit card required.
        </p>
      </section>

      {/* Features Section */}
      <section className="px-6 py-16 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Everything You Need for HOA Communications
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 text-emerald-700">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Preview */}
      <section className="px-6 py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
            Choose Your Template
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Select from our professionally designed templates. The Riverwood Newsletter maintains its original design,
            while other templates offer customization options.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Riverwood Newsletter', color: 'bg-emerald-700', locked: true },
              { name: 'Sample 1 - Fall/Winter', color: 'bg-amber-600', locked: false },
              { name: 'Sample 2 - Quarterly', color: 'bg-slate-700', locked: false },
              { name: 'Newsletter 2022', color: 'bg-teal-600', locked: false },
            ].map((template, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group">
                <div className={`h-40 ${template.color} flex items-center justify-center`}>
                  <FileText className="w-16 h-16 text-white/80 group-hover:scale-110 transition-transform" />
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900">{template.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {template.locked ? 'Fixed design' : 'Customizable'}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-emerald-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Create Your Newsletter?
          </h2>
          <p className="text-emerald-100 mb-8 text-lg">
            Join HOA boards across the country who trust our platform for their community communications.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/register')}
            className="bg-white text-emerald-700 hover:bg-emerald-50 px-10 py-6 text-lg"
          >
            Get Started Now
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 bg-gray-900">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-emerald-700 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <span className="text-white font-medium">HOA Newsletter Generator</span>
          </div>
          <p className="text-gray-400 text-sm">
            © 2025 HOA Newsletter Generator. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
